import { type ContactEmail } from "@/lib/types/Contact";

import {
  ActionConfirmationModal,
  Badge,
  Button,
  Label,
  SimpleSelectFormField,
  TextFormField,
  Typography,
} from "@/components/ui";

import { type createContactFormSchema } from "@/components/features/contact/AddOrEditContactModal/AddContactSlideover";
import { ContactFieldRowError } from "@/components/features/contact/ContactFieldRowError/ContactFieldRowError";
import { ContactPrimaryStarButton } from "@/components/features/contact/ContactPrimaryStarButton/ContactPrimaryStarButton";
import { useDeleteContactEmail } from "@/lib/data/react-query";
import { useContactPortalAccess } from "@/lib/data/react-query/contacts/contact-portal-access";
import { getContactEmailLabelOptions } from "@/lib/data/static/contactStatics";
import { ContactEmailIcon } from "@/lib/utils/iconUtils";
import pluralize from "pluralize";
import { useState } from "react";
import {
  useFieldArray,
  useWatch,
  type FieldPath,
  type UseFormReturn,
} from "react-hook-form";
import { type z } from "zod";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import {
  CheckCircle2,
  Clock3,
  MailCheck,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { AddOrEditContactEmailSlideover } from "./AddOrEditContactEmailSlideover";
import { type ContactType } from "@/lib/types/Contact";

const newClientRowId = (): string => {
  if (typeof globalThis.crypto.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2);
};

const matchesPortalEmail = (
  address?: string | null,
  portalEmail?: string | null,
) =>
  Boolean(
    address?.trim() &&
      portalEmail?.trim() &&
      address.trim().toLowerCase() === portalEmail.trim().toLowerCase(),
  );

function PortalEmailIndicator({ active }: { active: boolean }) {
  const label = active
    ? "Linked portal account email"
    : "Portal invitation sent to this email";
  const Icon = active ? MailCheck : Clock3;

  return (
    <TooltipWrapper message={label}>
      <span
        role="img"
        aria-label={label}
        tabIndex={0}
        className={
          active
            ? "inline-flex shrink-0 text-blue-600 dark:text-blue-400"
            : "inline-flex shrink-0 text-amber-600 dark:text-amber-400"
        }
      >
        <Icon className="h-4 w-4" aria-hidden="true" />
      </span>
    </TooltipWrapper>
  );
}

function ContactEmailsInlineFields({
  parentForm,
  hasCreatePermission,
  hasEditPermission,
  hasDeletePermission,
  hideLabel,
  organizationSubTypes,
  type,
  portalEmail,
  portalAccessActive,
  portalAccessUnknown,
}: {
  parentForm: UseFormReturn<z.infer<typeof createContactFormSchema>>;
  hasCreatePermission?: boolean;
  hasEditPermission?: boolean;
  hasDeletePermission?: boolean;
  hideLabel?: boolean;
  organizationSubTypes?: readonly string[];
  type?: ContactType;
  portalEmail?: string | null;
  portalAccessActive?: boolean;
  portalAccessUnknown?: boolean;
}) {
  const { control, register, getValues, setValue } = parentForm;
  const { fields, append, remove } = useFieldArray({
    control,
    name: "emails",
  });
  const watchedEmails = useWatch({ control, name: "emails" });
  const labelOptions = getContactEmailLabelOptions(type, organizationSubTypes);
  const [emailToRemove, setEmailToRemove] = useState<number | null>(null);

  const setPrimary = (index: number) => {
    const n = (getValues("emails") ?? []).length;
    for (let i = 0; i < n; i++) {
      setValue(`emails.${i}.isPrimary`, i === index, {
        shouldDirty: true,
      });
    }
  };

  const handleRemove = (index: number) => {
    if (!hasDeletePermission) {
      return;
    }
    const wasPrimary = getValues(`emails.${index}.isPrimary`);
    remove(index);
    if (wasPrimary) {
      const next = getValues("emails") ?? [];
      if (next.length > 0) {
        for (let i = 0; i < next.length; i++) {
          setValue(`emails.${i}.isPrimary`, i === 0, {
            shouldDirty: true,
          });
        }
      }
    }
  };

  const removeEmail = (index: number) => {
    const address = getValues(`emails.${index}.address`);
    if (
      (portalAccessUnknown && Boolean(address.trim())) ||
      matchesPortalEmail(address, portalEmail)
    ) {
      setEmailToRemove(index);
      return;
    }
    handleRemove(index);
  };

  const addRow = () => {
    if (!hasCreatePermission) {
      return;
    }
    const list = getValues("emails") ?? [];
    append({
      id: newClientRowId(),
      address: "",
      label: labelOptions[0] ?? "Personal",
      description: "",
      isPrimary: list.length === 0,
      dateModified: "",
    });
  };

  const emailCount = Math.max(fields.length, 1);
  const disabled = !hasEditPermission;

  return (
    <div>
      {!hideLabel && (
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <div className="flex items-center space-x-2">
            <ContactEmailIcon className="h-5 w-5 text-gray-400" />
            <Label className="text-lg font-semibold">
              {pluralize("Email", emailCount)}
            </Label>
          </div>
        </div>
      )}
      <div className="col-span-full">
        {fields.length > 0 && (
          <ul className="space-y-2">
            {fields.map((field, index) => {
              const row = watchedEmails?.[index];
              const isPrimary = !!row?.isPrimary;
              return (
                <li
                  key={field.id}
                  className="flex flex-col gap-1"
                  data-testid={
                    row?.id ? `contact-email-item-${row.id}` : undefined
                  }
                >
                  <div className="flex min-w-0 items-stretch gap-2 sm:gap-3">
                    {/*
                      Stretch columns to the same height, then center controls vertically so
                      the input, select, and icon row share one optical baseline (RHF
                      `FormItem` + hidden rows can otherwise nudge the input vs. the select).
                    */}
                    <div className="flex min-w-0 flex-1 flex-col justify-center">
                      <div className="flex min-w-0 items-center gap-2">
                        <TextFormField
                          className="min-w-0 flex-1 !space-y-0 [&>div:first-child]:hidden"
                          register={register(`emails.${index}.address`)}
                          placeholder="email@example.com"
                          disabled={disabled}
                          dataTestId="contact-email-address-input"
                        />
                        {matchesPortalEmail(row?.address, portalEmail) ? (
                          <PortalEmailIndicator
                            active={portalAccessActive === true}
                          />
                        ) : null}
                      </div>
                    </div>
                    <div className="flex w-32 min-w-0 flex-col justify-center self-stretch sm:w-36">
                      <SimpleSelectFormField
                        className="h-10 w-full min-w-0"
                        formItemClassName="!m-0 w-full min-w-0 !space-y-0"
                        placeholder="Label"
                        register={register(`emails.${index}.label`)}
                        items={labelOptions as readonly string[]}
                        disabled={disabled}
                      />
                    </div>
                    <div className="flex shrink-0 flex-col justify-center self-stretch">
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <ContactPrimaryStarButton
                          isPrimary={isPrimary}
                          disabled={disabled}
                          onClick={() => {
                            setPrimary(index);
                          }}
                          className="h-10 w-10"
                          ariaLabelCurrent="Primary email"
                          ariaLabelSet="Set as primary email"
                        />
                        {hasDeletePermission && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-10 w-10 shrink-0"
                            disabled={disabled}
                            onClick={() => {
                              removeEmail(index);
                            }}
                            aria-label="Remove email"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                  <ContactFieldRowError
                    control={parentForm.control}
                    fieldPath={
                      `emails.${index}.address` as FieldPath<
                        z.infer<typeof createContactFormSchema>
                      >
                    }
                  />
                </li>
              );
            })}
          </ul>
        )}
        {hasCreatePermission && (
          <div className="mt-2 w-full self-start">
            <Button
              type="button"
              variant="link"
              className="contact-subitem-add text-muted-foreground h-auto min-h-0 justify-start p-0"
              onClick={addRow}
              data-testid="add-email-button"
            >
              <Plus className="h-4 w-4 shrink-0" />
              Add email
            </Button>
          </div>
        )}
      </div>
      <ActionConfirmationModal
        open={emailToRemove !== null}
        setOpen={(open) => {
          if (!open) setEmailToRemove(null);
        }}
        title={portalAccessUnknown ? "Remove email?" : "Remove portal email?"}
        description={
          portalAccessUnknown
            ? "Portal status could not be checked. Remove this email from the contact?"
            : portalAccessActive
              ? "This email is used for this contact's portal account. Remove it from the contact?"
              : "A portal invitation was sent to this email. Remove it from the contact?"
        }
        buttonText="Remove email"
        afterActionFn={() => {
          if (emailToRemove !== null) handleRemove(emailToRemove);
          setEmailToRemove(null);
        }}
      />
    </div>
  );
}

export default function ContactEmailsSection({
  emails,
  contactId,
  hasCreatePermission,
  hasEditPermission,
  hasDeletePermission,
  hideLabel,
  parentForm,
  organizationSubTypes,
  type,
}: {
  emails?: ContactEmail[];
  contactId: string;
  hasCreatePermission?: boolean;
  hasEditPermission?: boolean;
  hasDeletePermission?: boolean;
  hideLabel?: boolean;
  parentForm?: UseFormReturn<z.infer<typeof createContactFormSchema>>;
  organizationSubTypes?: readonly string[];
  type?: ContactType;
}) {
  const deleteContactEmail = useDeleteContactEmail();
  const [activeEmailId, setActiveEmailId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const portalAccess = useContactPortalAccess(
    contactId,
    Boolean(contactId) && Boolean(hasDeletePermission),
  );
  const access = portalAccess.data?.data;
  const hasPendingInvitation =
    access?.status === "pending" &&
    (!access.expiresAt || new Date(access.expiresAt) > new Date());
  const portalEmail =
    access?.activeConnection || hasPendingInvitation
      ? access.invitedEmail
      : null;

  if (parentForm) {
    return (
      <ContactEmailsInlineFields
        parentForm={parentForm}
        hasCreatePermission={hasCreatePermission}
        hasEditPermission={hasEditPermission}
        hasDeletePermission={hasDeletePermission}
        hideLabel={hideLabel}
        organizationSubTypes={organizationSubTypes}
        type={type}
        portalEmail={portalEmail}
        portalAccessActive={access?.activeConnection}
        portalAccessUnknown={
          Boolean(contactId) && (portalAccess.isPending || portalAccess.isError)
        }
      />
    );
  }

  const emailList = emails ?? [];
  const parseSortableDate = (value: unknown) =>
    typeof value === "string" ? Date.parse(value) : Number.NaN;
  const sortedEmailList = emailList
    .map((email, index) => ({ email, index }))
    .sort((a, b) => {
      const aPrimary = "isPrimary" in a.email ? !!a.email.isPrimary : false;
      const bPrimary = "isPrimary" in b.email ? !!b.email.isPrimary : false;
      if (aPrimary !== bPrimary) {
        return aPrimary ? -1 : 1;
      }
      const aDate = parseSortableDate(a.email.dateModified);
      const bDate = parseSortableDate(b.email.dateModified);
      if (!Number.isNaN(aDate) && !Number.isNaN(bDate) && aDate !== bDate) {
        return aDate - bDate;
      }
      return a.index - b.index;
    })
    .map(({ email }) => email);
  const emailCount = Math.max(sortedEmailList.length, 1);
  const showEmptyState = emailList.length === 0 && !isAdding;

  const activeEmail = (emails ?? []).find(
    (email) => email.id === activeEmailId,
  );

  const handleDelete = (email: Pick<ContactEmail, "id">) => {
    deleteContactEmail.mutate({
      id: email.id,
      contactId: contactId,
    });
    setActiveEmailId(null);
  };

  return (
    <div>
      {!hideLabel && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center space-x-2">
            <ContactEmailIcon className="h-5 w-5 text-gray-400" />
            <Label className="text-lg font-semibold">
              {pluralize("Email address", emailCount)}
            </Label>
          </div>
          {hasCreatePermission && (
            <Button
              type="button"
              onClick={() => {
                setIsAdding(true);
              }}
              data-testid="add-email-button"
            >
              Create email
            </Button>
          )}
        </div>
      )}
      <div className="col-span-full">
        {hideLabel && hasCreatePermission && (
          <div className="flex justify-end">
            <Button
              type="button"
              onClick={() => {
                setIsAdding(true);
              }}
              data-testid="add-email-button"
            >
              Create email
            </Button>
          </div>
        )}
        {isAdding && (
          <div className="border-border bg-card mt-3 w-full rounded-md border p-3">
            <AddOrEditContactEmailSlideover
              parentForm={parentForm}
              editing={false}
              setOpen={setIsAdding}
              open={isAdding}
              contactId={contactId}
              hasEditPermission={hasEditPermission}
              organizationSubTypes={organizationSubTypes}
              type={type}
              variant="inline"
            />
          </div>
        )}
        {showEmptyState ? (
          <Typography
            variant="subHeading"
            className="text-muted-foreground mt-3"
          >
            No email addresses found.
          </Typography>
        ) : (
          <ul className="mt-3 space-y-3">
            {sortedEmailList.map((email, index) => {
              const isPrimary =
                "isPrimary" in email ? email.isPrimary : undefined;
              const label = email.label?.trim();
              const isActiveEdit = isEditing && email.id === activeEmailId;
              if (isActiveEdit) {
                return (
                  <li
                    key={email.id || `${email.address}-${index}`}
                    className="border-border bg-card w-full rounded-md border p-3"
                    data-testid={
                      email.id ? `contact-email-item-${email.id}` : undefined
                    }
                  >
                    <AddOrEditContactEmailSlideover
                      editing={true}
                      activeContactEmail={email}
                      setOpen={(nextOpen) => {
                        setIsEditing(nextOpen);
                        if (!nextOpen) {
                          setActiveEmailId(null);
                        }
                      }}
                      open={isEditing}
                      contactId={contactId}
                      hasEditPermission={hasEditPermission}
                      hasDeletePermission={hasDeletePermission}
                      onDelete={() => {
                        setIsDeleting(true);
                      }}
                      parentForm={parentForm}
                      organizationSubTypes={organizationSubTypes}
                      type={type}
                      variant="inline"
                    />
                  </li>
                );
              }
              return (
                <li
                  key={email.id || `${email.address}-${index}`}
                  className="border-border bg-card w-full rounded-md border p-3"
                  data-testid={
                    email.id ? `contact-email-item-${email.id}` : undefined
                  }
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      <Typography weight="medium" className="truncate">
                        {email.address || "-"}
                      </Typography>
                      {matchesPortalEmail(email.address, portalEmail) ? (
                        <PortalEmailIndicator
                          active={access?.activeConnection === true}
                        />
                      ) : null}
                      {label && (
                        <Badge variant="secondary" className="h-6 shrink-0">
                          {label}
                        </Badge>
                      )}
                      {Boolean(isPrimary) && (
                        <TooltipWrapper message="Primary">
                          <span className="flex items-center">
                            <CheckCircle2
                              className="h-4 w-4 text-emerald-600"
                              aria-hidden="true"
                            />
                            <span className="sr-only">Primary</span>
                          </span>
                        </TooltipWrapper>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        if (!email.id) {
                          return;
                        }
                        setIsEditing(true);
                        setActiveEmailId(email.id);
                      }}
                      disabled={!email.id}
                      aria-label="Edit email"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <ActionConfirmationModal
        title={`Are you sure you want to delete this email?`}
        description={
          portalAccess.isPending || portalAccess.isError
            ? "Portal status could not be checked. Delete this email from the contact?"
            : matchesPortalEmail(activeEmail?.address, portalEmail)
              ? access?.activeConnection
                ? "This email is used for this contact's portal account. Delete it from the contact?"
                : "A portal invitation was sent to this email. Delete it from the contact?"
              : undefined
        }
        open={isDeleting && !!activeEmail}
        setOpen={setIsDeleting}
        afterActionFn={() => {
          if (activeEmail) {
            handleDelete(activeEmail);
          }
        }}
      />
    </div>
  );
}
